import type {
  WorkbookDefinition,
  WorkbookDiagnostic,
  WorkbookDiagnosticLocation,
  WorkbookSheet,
} from "@erp/shared-types";
import { sheetKind } from "./sheetKinds";

function itemLabel(definition: WorkbookDefinition, itemId: string): string {
  const source = definition.sources.find((item) => item.id === itemId);
  if (source) return `Source '${source.name}'`;
  const sheet = definition.sheets.find((item) => item.id === itemId);
  return sheet ? `Worksheet '${sheet.name}'` : "Workbook";
}

/** The named thing a part is, else its position: "'Total'", "3". */
function named(name: string | undefined, index: number): string {
  return name ? `'${name}'` : String(index + 1);
}

const PIVOT_AREAS = {
  rows: "row field",
  columns: "column field",
  values: "value",
  filters: "filter on",
} as const;

/**
 * The name of the column, field or key a Diagnostic's part points at on its
 * Worksheet, if the Worksheet still has it. Its kind names its own parts.
 */
export function diagnosticPartName(
  sheet: WorkbookSheet | undefined,
  location: WorkbookDiagnosticLocation,
): string | undefined {
  if (!sheet) return undefined;
  if (location.part === "validation")
    return sheet.validations?.[location.index]?.column;
  return sheetKind(sheet).partName(location);
}

function partLabel(
  definition: WorkbookDefinition,
  location: WorkbookDiagnosticLocation,
): string {
  const sheet = definition.sheets.find((item) => item.id === location.itemId);
  const name = diagnosticPartName(sheet, location);
  switch (location.part) {
    case "item":
      return "";
    case "pivot":
      return `, ${PIVOT_AREAS[location.area]} ${named(name, location.index)}`;
    case "validation":
      return `, validation on ${named(name, location.index)}`;
    case "column":
      return `, column ${named(name, location.index)}`;
    case "lookup":
      return `, lookup ${location.index + 1}`;
    case "filter":
      return `, filter on ${named(name, location.index)}`;
    case "sort":
      return `, sort by ${named(name, location.index)}`;
    case "cell":
      return `, cell ${location.cell}`;
  }
}

/** One line naming where the problem is, for error messages and the agent. */
export function describeDiagnostic(
  definition: WorkbookDefinition,
  diagnostic: WorkbookDiagnostic,
): string {
  const { location } = diagnostic;
  return `${itemLabel(definition, location.itemId)}${partLabel(definition, location)}: ${diagnostic.message}`;
}
